import type { Finding } from './Findings';
import { ranked } from './Findings';
import type { AssignmentSyncPayload, StudentSyncRow } from './useAssignmentSyncState';
import { REASON_LABELS, STATE_LABELS } from './syncStates';

const LISTED = 20;

const PROBLEMS: StudentSyncRow['state'][] = ['never_synced', 'out_of_sync', 'retrying'];

const studentLine = (student: StudentSyncRow) => {
  const why = student.reason == null ? '' : `: ${REASON_LABELS[student.reason] ?? student.reason}`;
  const said = student.reason === 'unknown' && student.reason_detail != null ? ` (${student.reason_detail})` : '';

  return `- ${student.student_name} — ${STATE_LABELS[student.state]}${why}${said}`;
};

const findingLine = ({ owner, title }: Finding) => `- ${title}${owner ? ` [${owner.toUpperCase()}]` : ''}`;

/**
 * A plain-text write-up of what the assignment page shows, for a ticket or a reply: who, what is
 * wrong, which students are affected, and a link back. Zeros for missing work are owed, not grades
 * to recover, so they are left out of the affected list as they are out of the page's counts.
 */
const ticketSummary = (payload: AssignmentSyncPayload, url: string) => {
  const { assignment, diagnostics, students } = payload;
  const earned = students.filter(student => !student.zero_for_missing_work);
  const owed = earned.filter(student => student.state !== 'no_score' && student.state !== 'not_due');
  const delivered = owed.filter(student => student.state === 'in_sync').length;
  const affected = earned.filter(student => PROBLEMS.includes(student.state));

  return [
    `Grade sync: ${assignment.title} (assignment ${assignment.id})`,
    [assignment.staffer ?? 'No teacher', assignment.subscriber, assignment.lms_type ?? 'no LMS',
      assignment.courses.length > 0 ? `courses ${assignment.courses.join(', ')}` : 'not linked to a course',
    ].filter(Boolean).join(' · '),
    `${delivered} of ${owed.length} scores in the LMS`,
    ...(diagnostics.length > 0 ? ['', 'Found:', ...ranked(diagnostics).map(findingLine)] : []),
    ...(affected.length > 0 ? [
      '',
      `Students without their grade in the LMS (${affected.length}):`,
      ...affected.slice(0, LISTED).map(studentLine),
      ...(affected.length > LISTED ? [`- and ${affected.length - LISTED} more`] : []),
    ] : []),
    '',
    url,
  ].join('\n');
};

export default ticketSummary;
